'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';

import { LoadingIndicator } from '@/components/LoadingIndicator/LoadingIndicator';

import styles from './WistiaVideo.module.scss';

interface Props {
    id: string;
    autoPlay?: boolean;
    className?: string;
    onScriptLoad?: () => void;
}

if (typeof window !== 'undefined') {
    window._wq = [];
}

export const WISTIA_SCRIPT_URL = '//fast.wistia.com/assets/external/E-v1.js';

export function WistiaVideo({ id, autoPlay, className, onScriptLoad }: Props) {
    const videoRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // @ts-expect-error Argument of type 'RefObject<HTMLDivElement | null>' is not assignable to parameter of type 'RefObject<HTMLElement>'
    // `useIntersection` types are not yet updated to new React 19 types
    const intersection = useIntersection(videoRef, {
        root: null,
        rootMargin: '0px',
        threshold: 1,
    });
    const isInViewPort = intersection && intersection.intersectionRatio === 1;

    useEffect(() => {
        // Load wistia script on-demand only
        if (!isInViewPort || document.getElementById('wistia-script')) {
            return;
        }
        setIsLoading(true);
        const script = document.createElement('script');
        script.id = 'wistia-script';
        script.src = WISTIA_SCRIPT_URL;

        window._wq.push({
            id: '_all',
            onEmbedded: () => {
                setIsLoading(false);
                if (onScriptLoad) {
                    onScriptLoad();
                }
            },
        });
        document.body.append(script);
    }, [isInViewPort, onScriptLoad]);

    return (
        <>
            <div
                key={id}
                ref={videoRef}
                className={classNames(
                    styles.video,
                    'wistia_embed',
                    'videoFoam="true',
                    `wistia_async_${id}`,
                    {
                        [styles.loading]: isLoading,

                        'autoPlay=true': autoPlay,
                    },
                    className,
                )}
            />
            {isLoading && <LoadingIndicator className={styles.loadingIndicator} />}
        </>
    );
}
